import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { checkBleed, DEFAULT_BLEED_MM, SAFE_MM } from '../print/rules';
import { $, $in, setPressed } from './dom';
import { closeSheets } from './sheets';
import { toast } from './toast';

interface Preset { name: string; label: string; w?: number; h?: number; mm?: [number, number] }

export const PRESETS: Preset[] = [
  { name: 'โพสต์สี่เหลี่ยม', w: 1080, h: 1080, label: 'โพสต์ 1080×1080' },
  { name: 'โพสต์แนวตั้ง', w: 1080, h: 1350, label: 'แนวตั้ง 1080×1350' },
  { name: 'สตอรี่', w: 1080, h: 1920, label: 'สตอรี่ 1080×1920' },
  { name: 'A4 (210×297 mm)', mm: [210, 297], label: 'A4' },
  { name: 'A3 (297×420 mm)', mm: [297, 420], label: 'A3' },
  { name: 'A5 (148×210 mm)', mm: [148, 210], label: 'A5' }
];

export const PRINT_DPI = 300;
export const mmToPx = (mm: number) => Math.round(mm / 25.4 * PRINT_DPI);
export const MAX_SIDE_PX = 20000;

/** a preset's size in px */
export const presetPx = (p: Preset) => p.mm ? { w: mmToPx(p.mm[0]), h: mmToPx(p.mm[1]) } : { w: p.w!, h: p.h! };
/** "A4" / "โพสต์ 1080×1080" for a size that is a preset, else "2480 × 1200 px" */
export function sizeLabel(w: number, h: number): string {
  const p = PRESETS.find(x => { const s = presetPx(x); return s.w === w && s.h === h; });
  return p ? p.label : `${w} × ${h} px`;
}

/** Returns a Thai error message, or null when the size is allowed. */
export function checkDocSize(w: number, h: number): string | null {
  if (!(w >= 16 && h >= 16)) return 'ขนาดเล็กเกินไป ต้องอย่างน้อย 16 × 16 พิกเซล';
  if (w > MAX_SIDE_PX || h > MAX_SIDE_PX) return 'ขนาดใหญ่เกินไป (สูงสุด 20,000 พิกเซลต่อด้าน หรือประมาณ 169 ซม. ที่ 300 DPI)';
  return null;
}

/** whether the print guides are drawn is a per-device choice, not part of the design */
const GUIDES_KEY = 'craftstudio-print-guides';
function guidesWanted(): boolean { try { return localStorage.getItem(GUIDES_KEY) !== '0'; } catch { return true; } }
const MARKS_KEY = 'craftstudio-edited-marks';
function marksWanted(): boolean { try { return localStorage.getItem(MARKS_KEY) !== '0'; } catch { return true; } }
function rememberMarks(on: boolean) { try { localStorage.setItem(MARKS_KEY, on ? '1' : '0'); } catch { /* the choice just isn't remembered */ } }
function rememberGuides(on: boolean) { try { localStorage.setItem(GUIDES_KEY, on ? '1' : '0'); } catch { /* the choice just isn't remembered */ } }

/**
 * `clearAll`: the design back to one empty page (all pages, undoable); returns a Thai reason when it cannot.
 * Size and bleed are each page's own (หน้าหลายขนาด): with several pages, "เฉพาะหน้านี้ / ทุกหน้า" says which change.
 */
export function initDocSheet(ed: Editor, history: History, hooks: { onResized: () => void; clearAll: () => { pages: number; error: string | null }; onBleed: () => void }) {
  let unit: 'px' | 'mm' | 'cm' = 'px';
  let ownBleed = false;   // "กำหนดเอง" open
  let scope: 'this' | 'all' = 'all';
  let scopeFor = '';      // the page count + page the choice was made on (another page, or opening a design, starts from the default)
  const presetBox = $('dPreset');
  presetBox.innerHTML = PRESETS.map((p, i) => `<button data-i="${i}" aria-pressed="false">${p.label}</button>`).join('');

  /** which pages a change goes to; one page = that page */
  const which = (): 'this' | 'all' => history.pageCount() > 1 ? scope : 'all';
  const where = () => history.pageCount() <= 1 ? 'งาน' : scope === 'all' ? `ทุกหน้า (${history.pageCount()} หน้า)` : `หน้า ${history.currentPage() + 1}`;

  function syncScope() {
    const n = history.pageCount(), key = `${n}:${history.currentPage()}`;
    // default: every page while they are all one size (as before pages had sizes), else only the open page
    if (key !== scopeFor) { scope = history.mixed() ? 'this' : 'all'; scopeFor = key; }
    $('dScopeRow').hidden = n <= 1 || !!ed.doc.video;
    setPressed('dScope', b => b.dataset.v === scope);
    $('dScopeThis').textContent = `เฉพาะหน้านี้ (หน้า ${history.currentPage() + 1})`;
    $('dScopeAll').textContent = `ทุกหน้า (${n} หน้า)`;
    $('dScopeInfo').textContent = history.mixed()
      ? 'หน้าในงานนี้มีขนาดหรือระยะตัดตกต่างกัน ขนาดที่เห็นด้านล่างเป็นของหน้าที่เปิดอยู่'
      : 'ทุกหน้าขนาดเดียวกันอยู่ตอนนี้';
  }
  $('dScope').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    scope = b.dataset.v as typeof scope; syncScope();
  };

  function sync() {
    // the open page's size (a preset is matched by its size: each page can be another one)
    presetBox.querySelectorAll('button').forEach(b => {
      const s = presetPx(PRESETS[+b.dataset.i!]);
      b.setAttribute('aria-pressed', String(s.w === ed.doc.w && s.h === ed.doc.h));
    });
    syncScope();
    setPressed('cUnit', b => b.dataset.v === unit);
    $in('dBg').value = ed.doc.bg;
    $in('dName').value = ed.doc.name;
    syncBleed();
  }

  /* ---------- print bleed (outside the design, on every side) ---------- */
  function syncBleed() {
    const mm = ed.doc.bleedMm ?? 0;
    const preset = ['0', '3', '5'].includes(String(mm)) && !ownBleed;
    setPressed('dBleed', b => preset ? b.dataset.v === String(mm) : b.dataset.v === 'custom');
    $('dBleedOwn').hidden = preset;
    if (!preset) $in('dBleedMm').value = String(mm);
    $in('dGuides').checked = guidesWanted();
    $in('dEditMarks').checked = marksWanted();
    $('dBleedInfo').textContent = mm > 0
      ? `หน้างานขยายออกด้านละ ${mm} mm (เส้นประสีแดง) ภาพหรือพื้นที่ชิดขอบควรลากให้ถึงเส้นนี้ เส้นสีฟ้าคือขอบตัดจริง ตัวหนังสือควรอยู่ในเส้นประสีเทา (ห่างขอบตัด ${SAFE_MM} mm) ไฟล์ PNG/JPG ยังเป็นขนาดตัดจริง`
      : 'ไม่มีระยะตัดตก ถ้าส่งโรงพิมพ์ ภาพที่ชิดขอบอาจเห็นขอบขาวเมื่อตัดเหลื่อม';
  }
  function setBleed(mm: number) {
    const err = checkBleed(mm);
    if (err) { toast(err + ' ยังไม่ได้เปลี่ยน', true); return; }
    const why = history.resizePages(which(), { bleedMm: mm });
    if (why) { toast('เปลี่ยนระยะตัดตกไม่ได้: ' + why, true); return; }
    ownBleed = false; syncBleed(); syncScope(); hooks.onBleed();
    toast(mm > 0 ? `ตั้งระยะตัดตก ${mm} mm ให้${where()} แล้ว วัตถุเดิมไม่ขยับ` : `เอาระยะตัดตกของ${where()} ออกแล้ว`);
  }
  /** the check list's "ตั้งระยะตัดตก": the pages without bleed */
  function setBleedOn(pages: number[], mm: number) {
    const err = checkBleed(mm);
    if (err) { toast(err + ' ยังไม่ได้เปลี่ยน', true); return; }
    const why = history.setBleedOn(pages, mm);
    if (why) { toast('เปลี่ยนระยะตัดตกไม่ได้: ' + why, true); return; }
    syncBleed(); syncScope(); hooks.onBleed();
    toast(history.pageCount() > 1 ? `ตั้งระยะตัดตก ${mm} mm ให้หน้า ${pages.map(k => k + 1).join(', ')} แล้ว` : `ตั้งระยะตัดตก ${mm} mm แล้ว`);
  }
  $('dBleed').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    if (b.dataset.v === 'custom') { ownBleed = true; syncBleed(); $in('dBleedMm').focus(); return; }
    setBleed(Number(b.dataset.v));
  };
  $('dBleedApply').onclick = () => {
    const raw = $in('dBleedMm').value.trim();
    if (!raw) { toast('กรอกระยะตัดตกเป็นมิลลิเมตร (0 ถึง 10)', true); return; }
    setBleed(Number(raw));
  };
  $in('dGuides').onchange = e => {
    const on = (e.target as HTMLInputElement).checked;
    rememberGuides(on); ed.setGuides(on);
  };
  ed.setGuides(guidesWanted());
  // the "แต่งแล้ว" mark on edited pictures (src/editor/editor.ts drawEditedMarks)
  $in('dEditMarks').onchange = e => { const on = (e.target as HTMLInputElement).checked; rememberMarks(on); ed.setEditedMarks(on); };
  ed.setEditedMarks(marksWanted());

  /* design name: applied when the field is left or Enter is pressed */
  const nameIn = $in('dName');
  function applyName() {
    const v = nameIn.value.trim();
    if (v === ed.doc.name) return;
    if (!v) { toast('ชื่องานต้องไม่ว่าง', true); nameIn.value = ed.doc.name; return; }
    if (v.length > 120) { toast('ชื่องานยาวเกิน 120 ตัวอักษร', true); return; }
    ed.doc.name = v;
    hooks.onResized();
    toast(`เปลี่ยนชื่องานเป็น "${v}" แล้ว`);
  }
  nameIn.addEventListener('change', applyName);
  nameIn.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); nameIn.blur(); } });

  /** `forPrint`: a size in mm/cm — pages without bleed get the standard one (one undo step with the size) */
  function resize(w: number, h: number, name: string, forPrint = false) {
    const err = checkDocSize(w, h);
    if (err) { toast(err, true); return; }
    const before = history.pageSizes();
    const why = history.resizePages(which(), forPrint ? { w, h, bleedIfNone: DEFAULT_BLEED_MM } : { w, h });
    if (why) { toast('เปลี่ยนขนาดไม่ได้: ' + why, true); return; }
    ed.doc.sizeName = name;
    const addBleed = history.pageSizes().some((s, k) => (s.bleedMm ?? 0) > 0 && !((before[k]?.bleedMm ?? 0) > 0));
    sync(); hooks.onResized();
    toast(`ตั้งขนาด${where()} เป็น ${Math.round(w)} × ${Math.round(h)} px แล้ว วัตถุเดิมไม่ถูกย่อตาม${addBleed ? ` · ใส่ระยะตัดตก ${DEFAULT_BLEED_MM} mm ให้แล้ว (เปลี่ยนได้ด้านล่าง)` : ''}`);
  }

  presetBox.onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    const p = PRESETS[+b.dataset.i!];
    if (p.mm) resize(mmToPx(p.mm[0]), mmToPx(p.mm[1]), p.name, true); else resize(p.w!, p.h!, p.name);
  };
  $('cUnit').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    unit = b.dataset.v as typeof unit; sync();
  };
  $('cApply').onclick = () => {
    const w = parseFloat($in('cW').value), h = parseFloat($in('cH').value);
    if (!(w > 0) || !(h > 0)) { toast('กรอกความกว้างและความสูงเป็นตัวเลขที่มากกว่า 0', true); return; }
    const toPx = unit === 'mm' ? mmToPx : unit === 'cm' ? (v: number) => mmToPx(v * 10) : (v: number) => v;
    resize(toPx(w), toPx(h), unit === 'px' ? 'กำหนดเอง' : `กำหนดเอง ${w}×${h} ${unit}`, unit !== 'px');
  };
  $in('dBg').oninput = e => { ed.setDocBg((e.target as HTMLInputElement).value); hooks.onResized(); };

  /* two-tap confirm, since dialogs are not used */
  let armed: ReturnType<typeof setTimeout> | null = null;
  const newBtn = $('dNew');
  const label = 'เริ่มงานใหม่ (ล้างหน้างาน)';
  newBtn.onclick = () => {
    if (!armed) {
      newBtn.textContent = 'แตะอีกครั้งเพื่อยืนยันล้างหน้างาน';
      armed = setTimeout(() => { armed = null; newBtn.textContent = label; }, 3500);
      return;
    }
    clearTimeout(armed); armed = null; newBtn.textContent = label;
    ed.canvas.discardActiveObject();
    const r = hooks.clearAll();
    if (r.error) { toast('ล้างหน้างานไม่ได้: ' + r.error, true); return; }
    closeSheets();
    const one = history.pageSize(0);   // the page kept is page 1, at page 1's size
    toast(r.pages > 1 ? `ล้างทั้ง ${r.pages} หน้าแล้ว เหลือ 1 หน้าว่าง ขนาดเท่าหน้าแรก (${sizeLabel(one.w, one.h)}) กดเลิกทำถ้าต้องการกลับไป` : 'ล้างหน้างานแล้ว กดเลิกทำถ้าต้องการกลับไป');
  };

  /** a design was opened: "เฉพาะหน้านี้ / ทุกหน้า" starts from its default again */
  const resetScope = () => { scopeFor = ''; };
  return { sync, setBleed, setBleedOn, resetScope };
}
